import { useCallback, useMemo, useState } from "react";
import type { DrawerRouteContainerProps } from "shared-ui/src/components/detail-drawer/drawer-stack";
import {
  RoutedOverlayBase,
  useRoutedTab,
} from "shared-ui/src/hooks/routedOverlays";
import { Toast } from "shared-ui/src/components/Toast";
import { ProductDrawerTab } from "shared-ui/src/components/ecommerce/product-detail-drawer/types";
import type {
  IOrderLineItemDto,
  ProductDetailDrawerRequestDto,
} from "@lib/ShiOneClient";
import { resolveProductQuickViewTarget } from "@features/orders-features/functions/productQuickView";
import { useClaims } from "../../../../../../auth/ClaimsProvider";
import {
  StackedOrderProductDetailDrawer,
  type StackedOrderProductDetailDrawerProps,
} from "./StackedOrderProductDetailDrawer";
import { parseWebOrderId } from "@features/orders-features/components/order-approval-drawer/approvalReviewRoute";
import { useWebOrderApprovalDetail } from "@features/orders-features/hooks/useWebOrderApprovalDetail";
import { useInvoiceByNumber } from "@features/invoices-feature/hooks/useInvoiceByNumber";
import { invoiceLineQuickView } from "@features/invoices-feature/utilities/invoiceLineQuickView";
import {
  APPROVAL_DRAWER_SEGMENT,
  INVOICE_DRAWER_SEGMENT,
  ORDER_DRAWER_SEGMENT,
  PRODUCT_DRAWER_DEFAULT_TAB,
  PRODUCT_DRAWER_TABS,
} from "./orderDrawerSegments";
import {
  parseOrderRouteKey,
  parseProductRouteId,
  toProductRouteId,
  type ProductRouteTarget,
} from "./orderRouteKey";
import { useOrderDrawerData } from "./useOrderDrawerData";
import { useRouteNotFoundToast } from "shared-ui/src/hooks/routedOverlays/useRouteNotFoundToast";
import { useInvoiceDrawerReady } from "./InvoiceDrawerReadyContext";

export const PRODUCT_NOT_FOUND_MESSAGE =
  "Product details aren't available for this item.";

// A product opened from the orders grid has no order level to name its drawer section.
const PAGE_ROOTED_TELEMETRY_SECTION = "orders_quick_view";

/** Props pushed with the `product` route; cached for Back and Forward, gone after a refresh. */
export interface ProductDrawerPushProps {
  product: StackedOrderProductDetailDrawerProps["product"];
  request: ProductDetailDrawerRequestDto | null;
  telemetrySection?: string;
  currencyCode?: string | null;
  formatPrice?: (amount: number) => string;
}

const isProductDrawerPushProps = (
  value: Record<string, unknown> | undefined,
): value is Record<string, unknown> & ProductDrawerPushProps =>
  value != null && "product" in value && "request" in value;

function commonLineValue<T>(
  lines: readonly IOrderLineItemDto[],
  getValue: (line: IOrderLineItemDto) => T,
): T | undefined {
  const [first, ...others] = lines;
  if (!first) return undefined;
  const value = getValue(first);
  return others.every((line) => Object.is(getValue(line), value))
    ? value
    : undefined;
}

function productFromParentLines(
  lines: readonly IOrderLineItemDto[] | null | undefined,
  target: ProductRouteTarget,
  fromApproval: boolean,
): StackedOrderProductDetailDrawerProps["product"] {
  const routeId = toProductRouteId(
    target.productClass,
    target.productDescriptor,
  );
  const matches = lines?.filter((line) => {
    if (fromApproval) {
      const approvalTarget = resolveProductQuickViewTarget(line);
      return (
        approvalTarget != null &&
        toProductRouteId(
          approvalTarget.productClass,
          approvalTarget.productDescriptor,
        ) === routeId
      );
    }
    const descriptor =
      line.productDescriptor ??
      (line.shiPartNumber != null ? String(line.shiPartNumber) : null);
    const productClass =
      line.productClass || (descriptor ? "shicommodity" : null);
    return toProductRouteId(productClass, descriptor) === routeId;
  });
  if (!matches?.length) return null;

  // The URL identifies a product, not a specific line, so conflicting values are unknown.
  return {
    productDescriptor: target.productDescriptor,
    productClass: target.productClass,
    productName: commonLineValue(matches, (line) => line.productName),
    mfrPartNum: commonLineValue(matches, (line) => line.mfgPartNumber),
    price: commonLineValue(
      matches,
      (line) =>
        line.unitPrice ??
        (line.extendedPrice && line.quantity
          ? line.extendedPrice / line.quantity
          : line.extendedPrice),
    ),
  };
}

/**
 * Routed container for `.../product/{productClass}~{productDescriptor}/{tab}`. An in-app open
 * reuses the pushed order-line product. A deep link or refresh restores unambiguous parent-line
 * details and takes the org unit from the parent order or approval, which the Stock tab needs.
 */
export function RoutedProductDrawer({
  open,
  onClose,
  onCloseAll,
  zIndex,
  depth,
  routeId,
  levelPath,
  ancestors,
  cachedProps,
  onNotFound,
}: DrawerRouteContainerProps) {
  const invoiceDrawerReady = useInvoiceDrawerReady();
  const target = useMemo(() => parseProductRouteId(routeId), [routeId]);
  const pushed = isProductDrawerPushProps(cachedProps) ? cachedProps : null;
  const isCold = pushed == null;

  const parentLevel = ancestors.findLast(
    (level) =>
      level.segment === ORDER_DRAWER_SEGMENT ||
      level.segment === APPROVAL_DRAWER_SEGMENT ||
      level.segment === INVOICE_DRAWER_SEGMENT,
  );
  const orderRouteKey =
    parentLevel?.segment === ORDER_DRAWER_SEGMENT ? parentLevel.id : undefined;
  const orderIdentifier = useMemo(
    () => (orderRouteKey ? (parseOrderRouteKey(orderRouteKey) ?? null) : null),
    [orderRouteKey],
  );
  const approvalWebOrderId =
    parentLevel?.segment === APPROVAL_DRAWER_SEGMENT
      ? parseWebOrderId(parentLevel.id)
      : null;

  // Each lookup shares its query with the cold parent drawer below, so neither is a second request.
  const needsOrderLookup = isCold && target != null && orderIdentifier != null;
  const { isLoading: isOrderLoading, orderData } = useOrderDrawerData({
    identifier: orderIdentifier,
    preloadedData: null,
    enabled: open && needsOrderLookup,
  });
  const needsApprovalLookup =
    isCold && target != null && approvalWebOrderId != null;
  const { isLoading: isApprovalLoading, data: approvalData } =
    useWebOrderApprovalDetail(approvalWebOrderId, open && needsApprovalLookup);
  const invoiceNumber =
    parentLevel?.segment === INVOICE_DRAWER_SEGMENT ? parentLevel.id : null;
  const needsInvoiceLookup = isCold && target != null && !!invoiceNumber;
  const { isLoading: isInvoiceLoading, data: invoiceData } = useInvoiceByNumber(
    invoiceNumber,
    { enabled: open && needsInvoiceLookup },
  );
  const isResolvingOrgUnit =
    (needsOrderLookup && isOrderLoading) ||
    (needsApprovalLookup && isApprovalLoading) ||
    (needsInvoiceLookup && isInvoiceLoading);

  const { getClaimValue } = useClaims();
  const currentOrgUnitId =
    Number(getClaimValue("orgUnitIdCurrent")) || undefined;
  const coldOrgUnitId =
    (needsOrderLookup ? orderData?.orgUnitId : undefined) ??
    (needsApprovalLookup ? approvalData?.orgUnitId : undefined) ??
    (needsInvoiceLookup ? invoiceData?.orgUnitId : undefined) ??
    currentOrgUnitId;
  const coldCurrencyCode =
    (needsOrderLookup ? orderData?.currencyCode : undefined) ??
    (needsApprovalLookup ? approvalData?.currencyCode : undefined) ??
    (needsInvoiceLookup ? invoiceData?.currencyId : undefined);

  const invoiceProduct = useMemo(() => {
    if (!needsInvoiceLookup || !target) return null;
    return (
      invoiceData?.lines
        ?.map(invoiceLineQuickView)
        .find(
          (product) =>
            product?.productClass === target.productClass &&
            product.productDescriptor === target.productDescriptor,
        ) ?? null
    );
  }, [invoiceData, needsInvoiceLookup, target]);
  const parentProduct = useMemo(() => {
    if (!target) return null;
    if (needsOrderLookup) {
      return productFromParentLines(orderData?.allLineItems, target, false);
    }
    if (needsApprovalLookup) {
      return productFromParentLines(approvalData?.lineItems, target, true);
    }
    return invoiceProduct;
  }, [
    approvalData?.lineItems,
    invoiceProduct,
    needsApprovalLookup,
    needsOrderLookup,
    orderData?.allLineItems,
    target,
  ]);
  const coldProduct = useMemo(
    () =>
      parentProduct ??
      (target
        ? {
            productDescriptor: target.productDescriptor,
            productClass: target.productClass,
          }
        : null),
    [parentProduct, target],
  );
  const coldRequest = useMemo(
    () =>
      target && !isResolvingOrgUnit
        ? ({
            productDescriptor: target.productDescriptor,
            productClass: target.productClass,
            orgunitId: coldOrgUnitId,
          } as ProductDetailDrawerRequestDto)
        : null,
    [target, isResolvingOrgUnit, coldOrgUnitId],
  );

  // A cold drawer may still close as not found. Writing the default tab first would replace
  // that close navigation and leave the drawer open, so wait until the details have loaded.
  const [availableRouteId, setAvailableRouteId] = useState<string | null>(null);
  const handleDetailAvailable = useCallback(
    () => setAvailableRouteId(routeId),
    [routeId],
  );
  const { activeTab, setTab } = useRoutedTab({
    tabs: PRODUCT_DRAWER_TABS,
    defaultTab: PRODUCT_DRAWER_DEFAULT_TAB,
    basePath: levelPath,
    replaceHistory: false,
    ready:
      target != null &&
      (!isCold || parentProduct != null || availableRouteId === routeId),
  });

  useRouteNotFoundToast(
    target ? null : routeId,
    PRODUCT_NOT_FOUND_MESSAGE,
    onNotFound,
  );

  const handleDetailUnavailable = useCallback(() => {
    Toast(PRODUCT_NOT_FOUND_MESSAGE, "error");
    onNotFound();
  }, [onNotFound]);

  if (
    !target ||
    (parentLevel?.segment === INVOICE_DRAWER_SEGMENT &&
      invoiceDrawerReady != null &&
      invoiceDrawerReady.readyPath !== parentLevel.path)
  ) {
    return null;
  }

  return (
    <RoutedOverlayBase path={levelPath}>
      <StackedOrderProductDetailDrawer
        open={open}
        onClose={onClose}
        onCloseAll={onCloseAll}
        zIndex={zIndex}
        depth={depth}
        product={pushed ? pushed.product : coldProduct}
        request={pushed ? pushed.request : coldRequest}
        currencyCode={pushed ? pushed.currencyCode : coldCurrencyCode}
        formatPrice={pushed?.formatPrice}
        loading={isCold && isResolvingOrgUnit}
        telemetrySection={
          pushed?.telemetrySection ??
          (orderIdentifier ? undefined : PAGE_ROOTED_TELEMETRY_SECTION)
        }
        initialTab={
          activeTab === "stock"
            ? ProductDrawerTab.StockInventory
            : ProductDrawerTab.ProductDetails
        }
        onTabChange={(tab) =>
          setTab(tab === ProductDrawerTab.StockInventory ? "stock" : "details")
        }
        onDetailUnavailable={
          isCold && !parentProduct ? handleDetailUnavailable : undefined
        }
        onDetailAvailable={isCold ? handleDetailAvailable : undefined}
      />
    </RoutedOverlayBase>
  );
}
